import { CommonModule } from "@angular/common";
import { Component, ViewChildren, ElementRef, AfterViewInit, QueryList, HostListener, inject } from "@angular/core";
import { Router, IsActiveMatchOptions } from "@angular/router";
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconsProvider } from '../sidebar-icon-registry';
import { AuthenticationService } from "src/app/services/common/authentication.service";
import { SessionService } from "src/app/services/common/session.service";
import { AppLayoutService } from "../../app.layout.service";

interface SubMenuItem {
  name: string;
  url: string;
}

interface MenuItem {
  name: string;
  icon: string;
  type: 'single' | 'collapsable';
  url?: string;
  subMenuItems?: SubMenuItem[];
  expanded?: boolean;
  submenuHeight?: number;
}

interface MenuGroup {
  label: string;
  menuItems: MenuItem[];
}

@Component({
  selector: 'app-sidebar-content',
  templateUrl: './sidebar-content.html',
  styleUrls: ['./sidebar-content.scss'],
  providers: [iconsProvider],
  standalone: false
})
export class SidebarContent implements AfterViewInit {
  appLayoutService = inject(AppLayoutService);

  @ViewChildren('submenuContent') submenuElements!: QueryList<ElementRef>;

  menuGroups: MenuGroup[] = [
    {
      label: "Viszonteladói rendszer",
      menuItems: [
        {
          name: "Manuális eladás",
          icon: "lucideWallet",
          type: "collapsable",
          expanded: false,
          subMenuItems: [
            {
              name: "Hozzáférések",
              url: "/dashboard/my-packages"
            },
            {
              name: "Kiosztott hozzáférések",
              url: "/dashboard/redeemed-packages"
            },
          ]
        },

        {
          name: "Automatizált eladás",
          icon: "lucideSparkle",
          type: "collapsable",
          expanded: false,
          subMenuItems: [
            {
              name: "Saját oldalam",
              url: "/dashboard/my-vendor-selling-page"
            },
            {
              name: "Pénzügyek",
              url: "/dashboard/my-vendor-selling-page/finance"
            },
            {
              name: "Beállítások",
              url: "/dashboard/my-vendor-selling-page/settings"
            }
          ]
        },
        {
          name: "Számlák",
          icon: "lucideFileText",
          type: "single",
          url: "/dashboard/my-purchases"
        },
        {
          name: "Útmutató videók",
          icon: "lucideVideo",
          type: "single",
          url: "/dashboard/tutorial-videos"
        }
      ]
    }
  ];

  ngOnInit() {
    this.expandCollapsableMenuItemsIfActive();
  }

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private sessionService: SessionService) { }

  ngAfterViewInit() {
    this.measureSubmenuHeights();
  }

  /**
   * Akkor kell expandolni egy collapsable itemet, ha az aktuális route az benne található
   */
  private expandCollapsableMenuItemsIfActive() {
    this.menuGroups.forEach(group => {
      group.menuItems.forEach(menuItem => {
        const isCollapsable = menuItem.type === 'collapsable';
        const isActive = menuItem.subMenuItems?.some(subItem => {
          return this.router.isActive(subItem.url, { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' });
        });

        if (isCollapsable && isActive) {
          menuItem.expanded = true;
        }
      });
    });
  }

  @HostListener('window:resize')
  onResize() {
    // Reset submenu heights and re-measure on window resize
    this.getCollapsableMenuItems().forEach(item => {
      item.submenuHeight = undefined;
    });

    this.measureSubmenuHeights();
  }

  // Helper method to get all collapsable menu items
  private getCollapsableMenuItems(): MenuItem[] {
    let collapsableItems: MenuItem[] = [];

    this.menuGroups.forEach(group => {
      collapsableItems = [
        ...collapsableItems,
        ...group.menuItems.filter(item => item.type === 'collapsable')
      ];
    });

    return collapsableItems;
  }

  private measureSubmenuHeights() {
    setTimeout(() => {
      if (!this.submenuElements) return;

      const collapsableItems = this.getCollapsableMenuItems();

      this.submenuElements.forEach((submenu, index) => {
        if (index < collapsableItems.length) {
          collapsableItems[index].submenuHeight = submenu.nativeElement.scrollHeight;
        }
      });
    });
  }

  private collapseOtherMenuItems(item: MenuItem): void {
    this.menuGroups.forEach(group => {
      group.menuItems.forEach(menuItem => {
        if (menuItem !== item) {
          menuItem.expanded = false;
        }
      });
    });
  }


  toggleExpand(item: MenuItem): void {
    this.collapseOtherMenuItems(item);


    // Measure height if not already measured
    if (!item.expanded && !item.submenuHeight) {
      const collapsableItems = this.getCollapsableMenuItems();
      const index = collapsableItems.indexOf(item);

      if (index >= 0 && this.submenuElements && this.submenuElements.get(index)) {
        const submenuEl = this.submenuElements.get(index)!.nativeElement;
        const originalStyles = {
          visibility: submenuEl.style.visibility,
          display: submenuEl.style.display,
          maxHeight: submenuEl.style.maxHeight,
          opacity: submenuEl.style.opacity
        };

        // Make visible but hidden to measure
        submenuEl.style.visibility = 'hidden';
        submenuEl.style.display = 'block';
        submenuEl.style.maxHeight = 'none';
        submenuEl.style.opacity = '0';

        // Measure the height
        item.submenuHeight = submenuEl.scrollHeight;

        // Restore original styles
        submenuEl.style.visibility = originalStyles.visibility;
        submenuEl.style.display = originalStyles.display;
        submenuEl.style.maxHeight = originalStyles.maxHeight;
        submenuEl.style.opacity = originalStyles.opacity;
      }
    }

    item.expanded = !item.expanded;
  }

  public getLoggedInUserName() {
    return this.sessionService.getUsername();
  }

  public getLoggedInUserEmail() {
    return this.sessionService.getEmail();
  }


  openMrKreszWebsite() {
    window.open('https://mrkresz.hu', '_blank');
  }

  logout() {
    this.authenticationService.logout();
  }

  isMenuItemActive(item: MenuItem): boolean {
    const routerOptions: IsActiveMatchOptions = {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    };

    if (item.type === 'single' && item.url) {
      return this.router.isActive(item.url, routerOptions);
    } else if (item.type === 'collapsable' && item.subMenuItems) {
      return !!item.subMenuItems.find(subItem =>
        this.router.isActive(subItem.url, routerOptions)
      );
    }

    return false;
  }
}
